/**
 * home-redesign.js
 * Phase 2 — Sanity CMS integration for the redesigned homepage.
 *
 * Each render function:
 *  1. Checks the show/hide flag from Sanity; hides the section if false.
 *  2. Injects a Sanity edit link for editors.
 *  3. Replaces the section's inner container HTML with CMS data.
 *  4. Falls through to static HTML if data is missing (graceful degradation).
 */

import { client, urlFor, getSanityEditLink, resolveInternalLink } from './sanity-client';
import initScrollAnimations from './animations';

// ── Guard: only run on the redesigned homepage ────────────────────────────────
if (!document.getElementById('hp-hero')) {
  // Not the redesigned home page — exit immediately
} else {
  init();
}

function handleNavbarScroll() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;

  if (window.scrollY > 50) {
    navbar.classList.add('navbar-v3-scrolled');
    navbar.classList.remove('navbar-v3-transparent');
  } else {
    navbar.classList.remove('navbar-v3-scrolled');
    navbar.classList.add('navbar-v3-transparent');
  }
}

async function init() {
  const data = await fetchHomepageData();
  if (!data) return;

  // SEO
  if (data.seo?.title) document.title = data.seo.title;

  // Render each section (show/hide + content)
  renderHero(data);
  renderProblem(data);
  renderExpertise(data);
  renderMetrics(data);
  renderModels(data);
  renderTrust(data);
  renderClients(data);
  renderCaseStudies(data);
  renderCustomerReviews(data);
  renderInsights(data);
  renderCtaBanner(data);

  // Initialize navbar scroll transition
  window.addEventListener('scroll', handleNavbarScroll);
  handleNavbarScroll(); // Initial check

  // Re-initialize scroll animations after content injection
  initScrollAnimations();
}

// ── GROQ query ────────────────────────────────────────────────────────────────
async function fetchHomepageData() {
  try {
    const query = `*[_type == "homepage"][0]{
      _id,
      seo,
      showHeroSection, showProblemSection, showExpertiseSection,
      showMetricsSection, showModelsSection, showTrustSection,
      showClientsSection, showInsightsSection, showCtaBanner,
      showCaseStudies, showCustomerReviewsSection,
      "caseStudies": caseStudies[@->visibility != false]->{ _id, title, description, tag, tags, slug, image },
      "customerReviews": customerReviews[@->visibility != false]->{ _id, name, role, company, companyCategory, projectDomain, highlightQuote, fullTestimonial, externalLink },
      heroSection,
      problemSection,
      expertiseSection,
      metricsSection,
      modelsSection,
      trustSection { eyebrow, headline, points, stackImage, stackItems },
      clientsSection,
      insightsSection { eyebrow, headline, allInsightsLink, articles[] { tag, title, link, image } },
      customerReviewsSection { eyebrow, headline, description },
      ctaBanner
    }`;
    return await client.fetch(query);
  } catch (err) {
    console.error('[home-redesign] Sanity fetch failed:', err);
    return null;
  }
}

// ── Helper: show/hide a section ───────────────────────────────────────────────
function applyVisibility(sectionId, flag) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  if (flag === false) {
    el.classList.add('d-none');
  } else {
    el.classList.remove('d-none');
  }
}

// ── Helper: inject Sanity edit link into a section ────────────────────────────
function injectEditLink(sectionEl, docId, fieldName, label) {
  if (!sectionEl) return;
  if (sectionEl.querySelector('.sanity-edit-icon')) return; // already injected
  const link = getSanityEditLink(docId, 'homepage', fieldName, label);
  if (link) {
    sectionEl.classList.add('position-relative');
    sectionEl.insertAdjacentHTML('afterbegin', link);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 — Hero
// ─────────────────────────────────────────────────────────────────────────────
function renderHero(data) {
  applyVisibility('hp-hero', data.showHeroSection);
  if (data.showHeroSection === false) return;

  const section = document.getElementById('hp-hero');
  const container = document.getElementById('hp-hero-container');
  if (!container) return;

  injectEditLink(section, data._id, 'heroSection', 'Edit Hero');

  const h = data.heroSection;
  if (!h) return;

  // Hero image — when set on the Sanity doc, the section flips from the
  // centred single-column layout to a two-column diagonal split (text
  // on the left, image on the right with a clip-path slash on its left
  // edge). When absent, the existing centred hero renders unchanged.
  const heroImgSrc = h.graphicImage
    ? urlFor(h.graphicImage).width(900).quality(80).url()
    : null;

  // Toggle the modifier class on the section element — SCSS picks it up.
  if (section) {
    section.classList.toggle('has-hero-image', !!heroImgSrc);
  }

  const primaryLink  = resolveInternalLink(h.primaryCtaLink   || '/about/contact');
  const secondaryLink = resolveInternalLink(h.secondaryCtaLink || '/case-studies/');

  // Shared text block — same content in both layouts; only the wrapping
  // grid and the text-alignment differ via CSS.
  const textBlock = `
    <div class="hp-hero-v3__text">
      <span class="ct-label">${h.eyebrow || ''}</span>
      <h1 class="hp-hero-v3__headline">
        ${h.headline || ''}<br>
        <span class="ct-accent-text">${h.accentPhrase || ''}</span>
        ${h.headlineEnd ? ' ' + h.headlineEnd : ''}
      </h1>
      <p class="hp-hero-v3__sub">${h.subheadline || ''}</p>
      <div class="hp-hero-v3__ctas">
        <a href="${primaryLink}" class="btn-v3-primary cta-glow text-nowrap">
          ${h.primaryCtaText || 'Bring us your hardest problem'}
        </a>
        <a href="${secondaryLink}" class="btn-v3-secondary text-nowrap">
          ${h.secondaryCtaText || 'See our work'} <i class="fe fe-arrow-right ms-2"></i>
        </a>
      </div>
    </div>
  `;

  if (heroImgSrc) {
    container.innerHTML = `
      <div class="hp-hero-v3__grid">
        ${textBlock}
        <div class="hp-hero-v3__image" aria-hidden="true">
          <img src="${heroImgSrc}" alt="" />
        </div>
      </div>
    `;
    return;
  }

  // Fallback: original centred single-column hero. Wrap and centring
  // classes preserved here so `mx-auto` keeps the dek centred and
  // `justify-content-center` keeps the CTA row centred.
  container.innerHTML = `
    <div class="row align-items-center justify-content-center">
      <div class="col-12 col-lg-10 text-center hp-hero-v3__text hp-hero-v3__text--centered">
        <span class="ct-label">${h.eyebrow || ''}</span>
        <h1 class="hp-hero-v3__headline">
          ${h.headline || ''}<br>
          <span class="ct-accent-text">${h.accentPhrase || ''}</span>
          ${h.headlineEnd ? ' ' + h.headlineEnd : ''}
        </h1>
        <p class="hp-hero-v3__sub mx-auto">${h.subheadline || ''}</p>
        <div class="d-flex align-items-center justify-content-center flex-wrap" style="gap: 1.5rem;">
          <a href="${primaryLink}" class="btn-v3-primary cta-glow text-nowrap">
            ${h.primaryCtaText || 'Bring us your hardest problem'}
          </a>
          <a href="${secondaryLink}" class="btn-v3-secondary text-nowrap">
            ${h.secondaryCtaText || 'See our work'} <i class="fe fe-arrow-right ms-2"></i>
          </a>
        </div>
      </div>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 — Problem
// ─────────────────────────────────────────────────────────────────────────────
function renderProblem(data) {
  applyVisibility('hp-problem', data.showProblemSection);
  if (data.showProblemSection === false) return;

  const section = document.getElementById('hp-problem');
  const container = document.getElementById('hp-problem-container');
  if (!container) return;

  injectEditLink(section, data._id, 'problemSection', 'Edit Problem Section');

  const p = data.problemSection;
  if (!p) return;

  const lucideIcons = ['gauge', 'shield-check', 'code-2', 'bar-chart-3'];
  const cards = (p.problems || []).map((prob, i) => `
    <div class="col-12 col-sm-6 col-lg-2" data-animate="fade-up" data-delay="${(i + 1) * 100}">
      <div class="hp-problem-card-v3">
        <div class="hp-problem-card-v3__icon">
          <i data-lucide="${lucideIcons[i] || 'circle'}"></i>
        </div>
        <h4 class="hp-problem-card-v3__title">${prob.title || ''}</h4>
        <p class="hp-problem-card-v3__text">${prob.description || ''}</p>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="col-12 col-lg-4">
      <span class="ct-label" data-animate="fade-up">${p.eyebrow || ''}</span>
      <h2 class="hp-problem-v3__headline" data-animate="fade-up" data-delay="100">${p.headline || ''}</h2>
      <p class="hp-problem-v3__sub mb-4" data-animate="fade-up" data-delay="200">${p.subtext || ''}</p>
      <p class="hp-problem-v3__tagline" data-animate="fade-up" data-delay="300">${p.tagline || ''}</p>
    </div>
    ${cards}
  `;

  // Initialize Lucide icons with a robust retry mechanism
  const initLucide = () => {
    if (window.lucide) {
      window.lucide.createIcons();
    } else {
      setTimeout(initLucide, 100);
    }
  };
  initLucide();
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 — Expertise
// ─────────────────────────────────────────────────────────────────────────────
function renderExpertise(data) {
  applyVisibility('hp-expertise', data.showExpertiseSection);
  if (data.showExpertiseSection === false) return;

  const section = document.getElementById('hp-expertise');
  const container = document.getElementById('hp-expertise-container');
  if (!container) return;

  injectEditLink(section, data._id, 'expertiseSection', 'Edit Expertise Section');

  const e = data.expertiseSection;
  if (!e) return;

  const cards = (e.cards || []).map((card, i) => {
    const bullets = (card.bullets || []).map(b => `<li>${b}</li>`).join('');
    const hasLink = card.link && card.link !== '#' && card.link !== '';
    const linkHtml = hasLink ? `
      <a href="${resolveInternalLink(card.link)}" class="card-link">Learn more <i class="fe fe-arrow-right ms-2"></i></a>
    ` : '';

    return `
      <div class="col-12 col-md-4" data-animate="fade-up" data-delay="${(i + 1) * 100}">
        <div class="hp-expertise-card-v3">
          <p class="card-eyebrow">${card.eyebrow || ''}</p>
          <h3>${card.headline || ''}</h3>
          <ul>${bullets}</ul>
          ${linkHtml}
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = `
    <div class="text-center mb-5">
      <span class="ct-label" data-animate="fade-up">${e.eyebrow || ''}</span>
      <h2 class="fw-bold h1" data-animate="fade-up" data-delay="100">${e.headline || ''}</h2>
    </div>
    <div class="row g-4">${cards}</div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 — Metrics
// ─────────────────────────────────────────────────────────────────────────────
const metricIcons = ['history', 'briefcase', 'zap', 'users'];

function renderMetrics(data) {
  applyVisibility('hp-metrics', data.showMetricsSection);
  if (data.showMetricsSection === false) return;

  const section = document.getElementById('hp-metrics');
  const container = document.getElementById('hp-metrics-container');
  if (!container) return;

  injectEditLink(section, data._id, 'metricsSection', 'Edit Metrics');

  const m = data.metricsSection;
  if (!m) return;

  const stats = (m.stats || []).map((s, i) => `
    <div class="col-12 col-md-6 col-lg-3" data-animate="fade-up" data-delay="${(i + 1) * 100}">
      <div class="hp-metrics-v3__stat mb-5 mb-lg-0">
        <div class="d-flex align-items-start gap-3">
          <div class="stat-icon flex-shrink-0">
            <i data-lucide="${metricIcons[i] || 'star'}"></i>
          </div>
          <div>
            <p class="stat-value">${s.value || ''}</p>
            <p class="stat-label">${s.label || ''}</p>
            <p class="stat-desc">${s.description || ''}</p>
          </div>
        </div>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="hp-metrics-v3__intro">
      <span class="ct-label" data-animate="fade-up">${m.eyebrow || 'Impact that matters'}</span>
      <h2 class="fw-bold text-white" data-animate="fade-up" data-delay="100">${m.headline || 'Numbers that speak for themselves.'}</h2>
      <p data-animate="fade-up" data-delay="200">${m.description || ''}</p>
    </div>
    <div class="row g-0">${stats}</div>
  `;

  // Initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 — Partnership Models
// ─────────────────────────────────────────────────────────────────────────────
const modelIcons = ['users', 'package', 'rocket'];

function renderModels(data) {
  applyVisibility('hp-models', data.showModelsSection);
  if (data.showModelsSection === false) return;

  const section = document.getElementById('hp-models');
  const container = document.getElementById('hp-models-container');
  if (!container) return;

  injectEditLink(section, data._id, 'modelsSection', 'Edit Partnership Models');

  const m = data.modelsSection;
  if (!m) return;

  const cards = (m.models || []).map((model, i) => `
    <div class="col-12 col-md-4 hp-models-v3__col" data-animate="fade-up" data-delay="${(i + 1) * 100}">
      <div class="hp-models-v3__item">
        <div class="model-icon">
          <i data-lucide="${modelIcons[i] || 'star'}"></i>
        </div>
        <div class="model-content">
          <h3 class="model-title">${model.title || ''}</h3>
          <p class="model-desc">${model.description || ''}</p>
        </div>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="hp-models-v3__header mb-5 pb-4">
      <span class="ct-label" data-animate="fade-up">${m.eyebrow || 'How We Engage'}</span>
      <div class="row g-5 align-items-center">
        <div class="col-12 col-lg-6">
          <h2 class="hp-models-v3__headline" data-animate="fade-up" data-delay="100">${m.headline || 'Partnership models built for outcomes.'}</h2>
        </div>
        <div class="col-12 col-lg-6">
          <p class="hp-models-v3__description" data-animate="fade-up" data-delay="200">${m.description || ''}</p>
        </div>
      </div>
    </div>
    <div class="row g-0 hp-models-v3__grid">${cards}</div>
  `;

  // Initialize Lucide icons
  const initLucide = () => {
    if (window.lucide) {
      window.lucide.createIcons();
    } else {
      setTimeout(initLucide, 100);
    }
  };
  initLucide();
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6 — Why Trust Us
// ─────────────────────────────────────────────────────────────────────────────
function renderTrust(data) {
  applyVisibility('hp-trust', data.showTrustSection);
  if (data.showTrustSection === false) return;

  const section = document.getElementById('hp-trust');
  const container = document.getElementById('hp-trust-container');
  if (!container) return;

  injectEditLink(section, data._id, 'trustSection', 'Edit Trust Section');

  const t = data.trustSection;
  if (!t) return;

  const points = (t.points || []).map((pt, index) => `
    <div class="hp-trust-point-v3" data-animate="fade-up" data-delay="${(index + 1) * 100}">
      <div class="hp-trust-point-v3__icon">
        <i data-lucide="check"></i>
      </div>
      <div class="hp-trust-point-v3__content">
        <h3 class="hp-trust-point-v3__title">${pt.title || ''}</h3>
        <p class="hp-trust-point-v3__desc">${pt.description || ''}</p>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="hp-trust-v3__header mb-5 pb-2">
      <span class="ct-label" data-animate="fade-up">${t.eyebrow || 'Why teams trust us'}</span>
      <div class="row">
        <div class="col-12 col-lg-8">
          <h2 class="hp-trust-v3__headline" data-animate="fade-up" data-delay="100">${t.headline ? t.headline.replace('\\n', '<br/>') : 'Deep expertise.<br/>Thoughtful engineering.'}</h2>
        </div>
      </div>
    </div>
    <div class="row g-5">
      <div class="col-12 col-lg-6">
        <div class="hp-trust-v3__points">${points}</div>
      </div>
      <div class="col-12 col-lg-6" data-animate="scale-in" data-delay="200">
        <div class="hp-trust-v3__stack-container">
          ${t.stackImage ? `<img src="${urlFor(t.stackImage).url()}" alt="Engineering Stack" class="img-fluid rounded-4">` : '<div class="stack-placeholder"></div>'}
        </div>
      </div>
    </div>
  `;

  // Initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7 — Clients
// ─────────────────────────────────────────────────────────────────────────────
const clientIcons = ['shield', 'server', 'layers', 'lock'];

function renderClients(data) {
  applyVisibility('hp-clients', data.showClientsSection);
  if (data.showClientsSection === false) return;

  const section = document.getElementById('hp-clients');
  const container = document.getElementById('hp-clients-container');
  if (!container) return;

  injectEditLink(section, data._id, 'clientsSection', 'Edit Clients Section');

  const c = data.clientsSection;
  if (!c) return;

  const tiles = (c.tiles || []).map((tile, i) => `
    <div class="col-12 col-md-6 col-lg-3 hp-clients-v3__col" data-animate="fade-up" data-delay="${(i + 1) * 100}">
      <div class="hp-clients-v3__item">
        <div class="client-icon">
          <i data-lucide="${clientIcons[i] || 'star'}"></i>
        </div>
        <h3 class="client-title">${tile.title || ''}</h3>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="hp-clients-v3__header mb-5 pb-2">
      <span class="ct-label" data-animate="fade-up">${c.eyebrow || 'Who we work with'}</span>
      <div class="row">
        <div class="col-12 col-lg-8">
          <h2 class="hp-clients-v3__headline" data-animate="fade-up" data-delay="100">${c.headline || 'Built for teams building what matters.'}</h2>
        </div>
      </div>
    </div>
    <div class="row g-0 hp-clients-v3__grid">${tiles}</div>
  `;

  // Initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 8 — Insights
// ─────────────────────────────────────────────────────────────────────────────
function renderInsights(data) {
  applyVisibility('hp-insights', data.showInsightsSection);
  if (data.showInsightsSection === false) return;

  const section = document.getElementById('hp-insights');
  const container = document.getElementById('hp-insights-container');
  if (!container) return;

  injectEditLink(section, data._id, 'insightsSection', 'Edit Insights Section');

  const ins = data.insightsSection;
  if (!ins) return;

  const articles = (ins.articles || []).map((article, i) => `
    <div class="col-12 col-md-4" data-animate="fade-up" data-delay="${(i + 1) * 100}">
      <div class="hp-insights-v3__article">
        <div class="article-image">
          <img src="${article.image ? urlFor(article.image).width(240).height(240).url() : 'https://placehold.co/240x240/f5f5f5/999?text=Insight'}" alt="${article.title || 'Insight'}">
        </div>
        <div class="article-content">
          <h3 class="article-title">${article.title || ''}</h3>
          ${(article.link && article.link !== '#' && article.link !== '') ? `
            <a href="${resolveInternalLink(article.link)}" class="article-link">Read more <i data-lucide="arrow-right"></i></a>
          ` : ''}
        </div>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="hp-insights-v3__header mb-5 pb-4 d-flex justify-content-between align-items-end flex-wrap gap-4">
      <div data-animate="fade-up">
        <span class="ct-label">${ins.eyebrow || 'Big bites from our engineers'}</span>
        <h2 class="hp-insights-v3__headline">${ins.headline || 'Ideas, lessons, and perspectives.'}</h2>
      </div>
      <a href="${resolveInternalLink(ins.allInsightsLink || '#')}" class="ct-link-v3" data-animate="fade-in" data-delay="300">View all insights <i data-lucide="arrow-right"></i></a>
    </div>
    <div class="row g-5">${articles}</div>
  `;

  // Initialize Lucide icons
  if (window.lucide) {
    window.lucide.createIcons();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 9 — CTA Banner
// ─────────────────────────────────────────────────────────────────────────────
function renderCtaBanner(data) {
  applyVisibility('hp-cta', data.showCtaBanner);
  if (data.showCtaBanner === false) return;

  const section = document.getElementById('hp-cta');
  const container = document.getElementById('hp-cta-container');
  if (!container) return;

  injectEditLink(section, data._id, 'ctaBanner', 'Edit CTA Banner');

  const c = data.ctaBanner;
  if (!c) return;

  const link = resolveInternalLink(c.buttonLink || '/about/contact');

  container.innerHTML = `
    <h2 data-animate="fade-up">${c.headline || ''}</h2>
    <p data-animate="fade-up" data-delay="100">${c.subtext || ''}</p>
    <a href="${link}" class="btn-cta" data-animate="scale-in" data-delay="200">${c.buttonText || 'Talk to us →'}</a>
  `;
}
// ─────────────────────────────────────────────────────────────────────────────
// SECTION 10 — Case Studies
// ─────────────────────────────────────────────────────────────────────────────
function renderCaseStudies(data) {
  applyVisibility('hp-case-studies', data.showCaseStudies);
  if (data.showCaseStudies === false) return;

  const section = document.getElementById('hp-case-studies');
  const container = document.getElementById('hp-case-studies-container');
  if (!container) return;

  injectEditLink(section, data._id, 'caseStudies', 'Edit Case Studies');

  const caseStudies = data.caseStudies || [];
  if (caseStudies.length === 0) {
    container.innerHTML = '';
    return;
  }

  // Feature only the first 3 on the homepage to avoid clutter
  const featured = caseStudies.slice(0, 3);

  const cards = featured.map((study, i) => {
    const imageUrl = study.image ? urlFor(study.image).width(400).height(250).url() : 'https://placehold.co/400x250/f5f5f5/999?text=Case+Study';
    const detailLink = `/case-studies/case-study.html?title=${encodeURIComponent(study.title)}`;
    
    return `
      <div class="col-12 col-md-4" data-animate="fade-up" data-delay="${(i + 1) * 100}">
        <div class="hp-expertise-card-v3 h-100">
          <div class="mb-4 overflow-hidden rounded-3" style="height: 200px;">
            <img src="${imageUrl}" alt="${study.title}" class="img-fluid w-100 h-100 object-fit-cover">
          </div>
          <p class="card-eyebrow">${study.tag || 'Case Study'}</p>
          <h3 class="h4 mb-3">${study.title || ''}</h3>
          <p class="text-muted small mb-4">${study.description ? study.description.substring(0, 120) + '...' : ''}</p>
          <a href="${detailLink}" class="card-link mt-auto">Read case study <i class="fe fe-arrow-right ms-2"></i></a>
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = `
    <div class="hp-insights-v3__header mb-5 pb-4 d-flex justify-content-between align-items-end flex-wrap gap-4">
      <div data-animate="fade-up">
        <span class="ct-label">Proven Results</span>
        <h2 class="hp-insights-v3__headline">Case studies from the field.</h2>
      </div>
      <a href="/case-studies/" class="ct-link-v3" data-animate="fade-in" data-delay="300">View all case studies <i class="fe fe-arrow-right"></i></a>
    </div>
    <div class="row g-4">${cards}</div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 11 — Customer Reviews
// ─────────────────────────────────────────────────────────────────────────────
function renderCustomerReviews(data) {
  applyVisibility('hp-customer-reviews', data.showCustomerReviewsSection);
  if (data.showCustomerReviewsSection === false) return;

  const section = document.getElementById('hp-customer-reviews');
  const container = document.getElementById('hp-customer-reviews-container');
  if (!container) return;

  injectEditLink(section, data._id, 'customerReviews', 'Edit Customer Reviews');

  const reviews = data.customerReviews || [];
  if (reviews.length === 0) {
    container.innerHTML = '';
    return;
  }

  const totalReviews = reviews.length;
  let currentIndex = 0;
  let isHovered = false;
  let isModalOpen = false;

  const header = data.customerReviewsSection || {};

  // 1. Build the DOM exactly once
  let html = `
    <div class="accent-glow"></div>
    <div class="container-xl">
      <div class="header-content" data-animate="fade-up">
        <span class="eyebrow">${header.eyebrow || 'Client Testimonials'}</span>
        <h2 class="title">${header.headline || 'What engineering leaders say about Coriolis'}</h2>
        <p class="desc">${header.description || 'Real testimonials from VP-level engineering leaders and CTOs whose core systems, security, and cloud infrastructure we power.'}</p>
      </div>
    </div>

    <div class="slider-container" id="review-slider-wrapper" data-animate="fade-up" data-delay="100">
      <button id="review-slider-prev-btn" class="slider-nav-btn prev-btn" aria-label="Previous Reviews">
        <i data-lucide="chevron-left"></i>
      </button>
      <button id="review-slider-next-btn" class="slider-nav-btn next-btn" aria-label="Next Reviews">
        <i data-lucide="chevron-right"></i>
      </button>
  `;

  reviews.forEach((review, idx) => {
    html += `
      <div class="review-card is-hidden" id="review-card-${idx}" data-idx="${idx}">
        <div class="card-top">
          <span class="domain">${review.projectDomain || ''}</span>
        </div>
        <div class="card-quote">
          <p>"${review.highlightQuote || ''}"</p>
        </div>
        <div class="card-bottom">
          <div class="author-info">
            <h4>${review.name || ''}</h4>
            <div class="role">${review.role || ''}</div>
            <div class="company-info">
              <i data-lucide="building-2"></i>
              <span class="company">${review.company || ''}</span>
            </div>
          </div>
          <div class="actions">
            <button class="btn-view" data-index="${idx}">
              <span>View Review</span>
              <i data-lucide="arrow-right"></i>
            </button>
            ${review.externalLink ? `
            <a href="${review.externalLink}" target="_blank" class="btn-external" aria-label="External link">
              <i data-lucide="external-link"></i>
            </a>` : ''}
          </div>
        </div>
      </div>
    `;
  });

  html += `</div><div class="slider-dots" id="review-slider-dots" data-animate="fade-up" data-delay="200">`;
  
  reviews.forEach((_, idx) => {
    html += `<button id="review-dot-${idx}" data-dot-index="${idx}" aria-label="Go to slide ${idx + 1}"></button>`;
  });

  html += `</div>`;

  // Modal overlay
  html += `
    <div id="review-modal-overlay" class="review-modal-overlay">
      <div class="review-modal-content" id="review-modal-content">
        <!-- Content populated dynamically -->
      </div>
    </div>
  `;

  container.innerHTML = html;

  // Initialize animations and icons once the DOM is injected
  if (window.lucide) window.lucide.createIcons();
  
  // Need to dynamically import or call global animation trigger if available
  // It's safer to dispatch a custom event or check window for the init function.
  // The main script calls initScrollAnimations() after all renders, so we are covered there.

  const wrapper = document.getElementById('review-slider-wrapper');
  
  // 2. Update logic: Only modify classes and styles of existing elements
  const updateDOM = () => {
    reviews.forEach((_, idx) => {
      const card = document.getElementById(`review-card-${idx}`);
      const dot = document.getElementById(`review-dot-${idx}`);
      
      const currentMod = ((currentIndex % totalReviews) + totalReviews) % totalReviews;
      let offset = (idx - currentMod + totalReviews) % totalReviews;
      if (offset > totalReviews / 2) offset -= totalReviews;

      const isCenter = offset === 0;
      const isAdjacent = Math.abs(offset) === 1;

      // Handle wrap around jump to prevent crossing the center
      const prevOffset = card.hasAttribute('data-offset') ? parseInt(card.getAttribute('data-offset')) : offset;
      if (Math.abs(prevOffset - offset) > 1 && totalReviews > 2) {
        // If jumping to a visible adjacent spot, start the jump from further out so it slides in
        let startOffset = offset;
        if (offset === -1) {
          startOffset = -2;
        } else if (offset === 1) {
          startOffset = 2;
        }

        card.style.transition = 'none';
        
        // Apply the hidden starting position
        if (Math.abs(startOffset) > 1) {
          card.className = 'review-card is-hidden';
        }
        const startXPercent = startOffset * 100;
        const startXPx = startOffset * 24;
        card.style.transform = `translateX(calc(${startXPercent}% + ${startXPx}px)) scale(1)`;
        card.style.zIndex = 20 - Math.abs(startOffset) * 5;
        
        card.offsetHeight; // force reflow
        card.style.transition = '';
      } else {
        card.style.transition = '';
      }

      if (isCenter) {
        card.className = 'review-card is-center';
      } else if (isAdjacent) {
        card.className = 'review-card is-adjacent';
      } else {
        card.className = 'review-card is-hidden';
      }

      const xPercent = offset * 100;
      const xPx = offset * 24;
      card.style.transform = `translateX(calc(${xPercent}% + ${xPx}px)) scale(1)`;
      card.style.zIndex = 20 - Math.abs(offset) * 5;
      card.setAttribute('data-offset', offset);

      if (dot) {
        dot.className = (currentMod === idx) ? 'active' : '';
      }
    });
  };

  // 3. Attach Events Once
  document.getElementById('review-slider-prev-btn').addEventListener('click', () => {
    currentIndex -= 1;
    updateDOM();
  });
  
  document.getElementById('review-slider-next-btn').addEventListener('click', () => {
    currentIndex += 1;
    updateDOM();
  });
  
  wrapper.addEventListener('mouseenter', () => { isHovered = true; });
  wrapper.addEventListener('mouseleave', () => { isHovered = false; });



  wrapper.querySelectorAll('.btn-view').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.getAttribute('data-index'));
      openModal(reviews[idx]);
    });
  });

  document.querySelectorAll('.slider-dots button').forEach(dot => {
    dot.addEventListener('click', () => {
      const idx = parseInt(dot.getAttribute('data-dot-index'));
      const currentMod = ((currentIndex % totalReviews) + totalReviews) % totalReviews;
      let shift = idx - currentMod;
      if (shift > totalReviews / 2) shift -= totalReviews;
      if (shift < -totalReviews / 2) shift += totalReviews;
      currentIndex += shift;
      updateDOM();
    });
  });

  const openModal = (review) => {
    isModalOpen = true;
    const overlay = document.getElementById('review-modal-overlay');
    const content = document.getElementById('review-modal-content');
    
    content.innerHTML = `
      <div class="modal-header">
        <div class="author-info">
          <h4>${review.name || ''}</h4>
          <div class="role">${review.role || ''}</div>
          <div class="company-info">
            <i data-lucide="building-2"></i>
            <span class="company">${review.company || ''}</span>
            <span class="dot">•</span>
            <span>${review.companyCategory || ''}</span>
          </div>
        </div>
        <button class="btn-close" id="btn-close-modal" aria-label="Close modal">
          <i data-lucide="x"></i>
        </button>
      </div>
      <div class="modal-quote">
        <i data-lucide="quote" class="quote-icon"></i>
        <p>"${review.highlightQuote || ''}"</p>
      </div>
      <div class="modal-story">
        <p>${review.fullTestimonial || ''}</p>
      </div>
    `;
    if (window.lucide) window.lucide.createIcons();
    document.getElementById('btn-close-modal').addEventListener('click', closeModal);
    overlay.classList.add('show');
  };

  const closeModal = () => {
    isModalOpen = false;
    document.getElementById('review-modal-overlay').classList.remove('show');
  };

  document.getElementById('review-modal-overlay').addEventListener('click', closeModal);
  document.getElementById('review-modal-content').addEventListener('click', (e) => e.stopPropagation());

  // Timer loop
  setInterval(() => {
    if (!isHovered && !isModalOpen) {
      currentIndex += 1;
      updateDOM();
    }
  }, 6000);

  // Initial draw
  updateDOM();
}
